import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertTriangle, Clock, FileSignature, BadgeCheck, FilePlus2,
  MailQuestion, MessageSquare, HardHat, Activity, ChevronRight, Filter,
  RefreshCw, Flame, TrendingDown, ArrowUpRight, ShieldAlert, Users, BadgePoundSterling, CalendarClock, UserPlus,
  ClipboardCheck, Wrench, Mail, CalendarDays,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { fmtMoney } from "@/pages/admin/util";
import { SLA_TONE, STATUS_LABEL, shortId, relTime } from "@/pages/admin/workspace/util";

/* ---------- Operations Command Centre ----------
 * Replaces the legacy KPI-tile dashboard. Answers six questions every
 * operations director asks at the start of a day:
 *
 *   1. What needs attention today?
 *   2. What is overdue?
 *   3. What is waiting on someone else?
 *   4. Which contractors are causing issues?
 *   5. Which jobs are at risk?
 *   6. Which invoices are blocked?
 *
 * Layout: dense, dark-navy hero banner with the 4 most-urgent counters,
 * action queues below in a 2-column grid, each row clicking through to
 * /adminpanel/workspace/{id}.
 * --------------------------------------------------------------------- */
export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [membership, setMembership] = useState(null);
  const [healthKpis, setHealthKpis] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("ALL"); // ALL | HIGH_VALUE | TODAY
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [ops, ms, hc] = await Promise.all([
        adminApi.get("/ops-dashboard"),
        adminApi.get("/membership-dashboard").catch(() => ({ data: null })),
        adminApi.get("/health-check-kpis").catch(() => ({ data: null })),
      ]);
      setData(ops.data);
      setMembership(ms.data);
      setHealthKpis(hc.data);
      setErr("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load command centre");
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    let active = true;
    (async () => { if (active) await load(); })();
    const id = setInterval(() => { load(); }, 60_000);
    return () => { active = false; clearInterval(id); };
  }, [load]);

  const applyFilter = (rows) => {
    if (!rows) return [];
    if (filter === "HIGH_VALUE") return rows.filter((r) => r.high_value);
    if (filter === "TODAY") {
      const today = new Date().toISOString().slice(0, 10);
      return rows.filter((r) => (r.created_at || "").startsWith(today));
    }
    return rows;
  };

  if (err) return <div className="text-[#B91C1C]">{err}</div>;
  if (!data) return <div className="flex items-center gap-2 text-[#475569]"><Loader2 className="animate-spin" size={16}/> Loading command centre…</div>;

  const h = data.headline;
  const generated = new Date(data.generated_at);

  return (
    <div data-testid="admin-ops-dashboard" className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8">
      {/* HERO COMMAND STRIP --------------------------------------------------- */}
      <header className="bg-[#071A33] text-white px-4 sm:px-6 lg:px-8 py-9 border-b border-black/40">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Operations Command Centre</p>
            <h1 className="font-display text-[34px] lg:text-[48px] xl:text-[56px] leading-[1.02] tracking-tight text-white mt-2" data-testid="ops-title">What needs attention today.</h1>
            <p className="text-[13px] text-white/55 mt-2">
              Live · refreshed {relTime(data.generated_at)} ·
              <button onClick={load} className="text-white/70 hover:text-white inline-flex items-center gap-1 ml-2 underline-offset-2 hover:underline" data-testid="ops-refresh">
                <RefreshCw size={11} className={refreshing ? "animate-spin" : ""}/> refresh
              </button>
            </p>
          </div>
          <FilterChips value={filter} onChange={setFilter}/>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4">
          <Counter
            testid="ops-counter-overdue"
            value={h.overdue} label="Overdue · SLA breached"
            icon={<AlertTriangle size={18}/>}
            tone={h.overdue > 0 ? "red" : "muted"}
            anchor="#overdue"
          />
          <Counter
            testid="ops-counter-at-risk"
            value={h.at_risk} label="At risk · 25% SLA left"
            icon={<Clock size={18}/>}
            tone={h.at_risk > 0 ? "amber" : "muted"}
            anchor="#at-risk"
          />
          <Counter
            testid="ops-counter-resident"
            value={h.resident_updates} label="Resident replies waiting"
            icon={<MessageSquare size={18}/>}
            tone={h.resident_updates > 0 ? "primary" : "muted"}
            anchor="#resident"
          />
          <Counter
            testid="ops-counter-contractors"
            value={h.contractor_issues} label="Contractors with issues"
            icon={<HardHat size={18}/>}
            tone={h.contractor_issues > 0 ? "amber" : "muted"}
            anchor="#contractors"
          />
        </div>
      </header>

      {/* Section banner — distinguishes the counters from the queues. */}
      <div className="px-4 sm:px-6 lg:px-8 pt-8 pb-3">
        <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Recurring Revenue</p>
        <h2 className="font-display text-2xl text-[#071A33] mt-1">Membership snapshot.</h2>
      </div>

      {membership?.kpis && (
        <div className="px-4 sm:px-6 lg:px-8 mb-3 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ops-membership-strip">
          <MembershipTile testid="ops-members-active" label="Active members" value={membership.kpis.active} icon={<Users size={14}/>} tone="green" link="/adminpanel/members?status=ACTIVE"/>
          <MembershipTile testid="ops-members-new" label="New this month" value={membership.kpis.new_this_month} icon={<UserPlus size={14}/>} tone="teal" link="/adminpanel/members"/>
          <MembershipTile testid="ops-members-expiring" label="Expiring in 30 days" value={membership.kpis.expiring_30_days} icon={<CalendarClock size={14}/>} tone={membership.kpis.expiring_30_days > 0 ? "amber" : "muted"} link="/adminpanel/members?status=ACTIVE"/>
          <MembershipTile testid="ops-members-past-due" label="Past due" value={membership.kpis.past_due} icon={<AlertTriangle size={14}/>} tone={membership.kpis.past_due > 0 ? "red" : "muted"} link="/adminpanel/members?status=PAST_DUE"/>
          <MembershipTile testid="ops-members-mrr" label="Est. MRR" value={fmtMoney(membership.kpis.mrr_estimate)} icon={<BadgePoundSterling size={14}/>} tone="muted" link="/adminpanel/members"/>
        </div>
      )}

      {/* Section banner — Annual Health Check operations. */}
      {healthKpis && (
        <>
          <div className="px-4 sm:px-6 lg:px-8 pt-8 pb-3 flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Annual Health Checks</p>
              <h2 className="font-display text-2xl text-[#071A33] mt-1">Inspection programme.</h2>
            </div>
            <Link to="/adminpanel/health-checks" className="text-xs text-[#064E2E] hover:underline inline-flex items-center gap-1" data-testid="ops-hc-open">
              Open Health Checks <ChevronRight size={12}/>
            </Link>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 mb-3 grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="ops-healthcheck-strip">
            <MembershipTile testid="ops-hc-due" label="Due this month" value={healthKpis.due_this_month} icon={<CalendarDays size={14}/>} tone={healthKpis.due_this_month > 0 ? "amber" : "muted"} link="/adminpanel/health-checks?status=SCHEDULED"/>
            <MembershipTile testid="ops-hc-scheduled" label="Scheduled" value={healthKpis.scheduled} icon={<CalendarClock size={14}/>} tone="teal" link="/adminpanel/health-checks?status=SCHEDULED"/>
            <MembershipTile testid="ops-hc-completed" label="Completed (mo.)" value={healthKpis.completed_this_month} icon={<ClipboardCheck size={14}/>} tone="green" link="/adminpanel/health-checks?status=COMPLETED"/>
            <MembershipTile testid="ops-hc-overdue" label="Overdue" value={healthKpis.overdue} icon={<AlertTriangle size={14}/>} tone={healthKpis.overdue > 0 ? "red" : "muted"} link="/adminpanel/health-checks"/>
            <MembershipTile testid="ops-hc-amberred" label="Amber / Red rated" value={`${healthKpis.amber_rated} · ${healthKpis.red_rated}`} icon={<ShieldAlert size={14}/>} tone={healthKpis.red_rated > 0 ? "red" : healthKpis.amber_rated > 0 ? "amber" : "muted"} link="/adminpanel/health-checks?status=COMPLETED"/>
            <MembershipTile testid="ops-hc-jobs" label="Jobs generated" value={healthKpis.jobs_generated} icon={<Wrench size={14}/>} tone="muted" link="/adminpanel/support-cases?source=annual_health_check"/>
          </div>
          {(healthKpis.urgent_red?.length > 0 || healthKpis.recent_completed?.length > 0) && (
            <div className="px-4 sm:px-6 lg:px-8 mb-3 grid lg:grid-cols-2 gap-3">
              {healthKpis.urgent_red?.length > 0 && (
                <div className="bg-white border border-[#B91C1C]/25 rounded-xl overflow-hidden" data-testid="ops-hc-urgent-list">
                  <header className="px-4 py-3 border-b border-[#E2DCD0] bg-[#B91C1C]/5 flex items-center justify-between">
                    <p className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#B91C1C]">Urgent · RED rated</p>
                    <span className="font-mono text-[10px] text-[#B91C1C]">{healthKpis.urgent_red.length}</span>
                  </header>
                  <ul className="divide-y divide-[#E2DCD0]">
                    {healthKpis.urgent_red.slice(0, 5).map((r) => (
                      <li key={r.id} className="px-4 py-2.5">
                        <Link to={`/adminpanel/health-checks/${r.id}`} className="block hover:bg-[#F7F4ED]/70 -mx-4 px-4" data-testid={`ops-hc-red-${r.id}`}>
                          <p className="text-sm text-[#071A33] font-medium truncate">{r.member_name}</p>
                          <p className="text-[10.5px] text-[#475569] truncate">{r.park_name || r.property_address || ""}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {healthKpis.recent_completed?.length > 0 && (
                <div className="bg-white border border-[#E2DCD0] rounded-xl overflow-hidden" data-testid="ops-hc-recent-list">
                  <header className="px-4 py-3 border-b border-[#E2DCD0] bg-[#F7F4ED]/60 flex items-center justify-between">
                    <p className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#475569]">Recently completed</p>
                    <span className="font-mono text-[10px] text-[#475569]">{healthKpis.reports_sent || 0} emailed</span>
                  </header>
                  <ul className="divide-y divide-[#E2DCD0]">
                    {healthKpis.recent_completed.slice(0, 5).map((r) => (
                      <li key={r.id} className="px-4 py-2.5">
                        <Link to={`/adminpanel/health-checks/${r.id}`} className="block hover:bg-[#F7F4ED]/70 -mx-4 px-4" data-testid={`ops-hc-recent-${r.id}`}>
                          <div className="flex items-baseline justify-between gap-2 flex-wrap">
                            <p className="text-sm text-[#071A33] font-medium truncate">{r.member_name}</p>
                            <p className="text-[10px] text-[#475569] whitespace-nowrap">{(r.completed_at || "").slice(0, 10)}</p>
                          </div>
                          <p className="text-[10.5px] text-[#475569] truncate">
                            {r.park_name || r.property_address || ""}{r.overall_rating && <> · <span className={
                              r.overall_rating === "RED" ? "text-[#B91C1C]" :
                              r.overall_rating === "AMBER" ? "text-[#B88A2A]" : "text-[#064E2E]"
                            }>{r.overall_rating}</span></>}
                          </p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Section banner — distinguishes the counters from the queues. */}
      <div className="px-4 sm:px-6 lg:px-8 pt-8 pb-3">
        <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Priority Queues</p>
        <h2 className="font-display text-2xl text-[#071A33] mt-1">Action lists by who is blocking.</h2>
      </div>

      {/* ACTION QUEUES ----------------------------------------------------- */}
      <div className="px-4 sm:px-6 lg:px-8 pb-9 grid lg:grid-cols-2 gap-5">

        <Queue id="overdue" title="Overdue jobs — fix the breach"
               subtitle="Sorted by hours past SLA. Click a row to open the workspace."
               icon={<AlertTriangle size={14}/>}
               accent="bg-[#B91C1C]"
               count={h.overdue} testid="ops-overdue">
          {applyFilter(data.overdue).slice(0, 12).map((c) => <CaseRow key={c.id} c={c} tone="red"/>)}
          <EmptyMsg show={applyFilter(data.overdue).length === 0} text="No overdue jobs. Beautiful."/>
        </Queue>

        <Queue id="at-risk" title="At-risk jobs — act before they breach"
               subtitle="Less than 25% of SLA window remaining."
               icon={<Clock size={14}/>}
               accent="bg-[#B88A2A]"
               count={h.at_risk} testid="ops-atrisk">
          {applyFilter(data.at_risk).slice(0, 12).map((c) => <CaseRow key={c.id} c={c} tone="amber"/>)}
          <EmptyMsg show={applyFilter(data.at_risk).length === 0} text="Nothing nearing breach."/>
        </Queue>

        <Queue id="awaiting-quote" title="Awaiting quote · chase contractor"
               subtitle="Sorted by longest idle. Contractor has the ball."
               icon={<FileSignature size={14}/>}
               accent="bg-[#0F766E]"
               count={h.awaiting_quote} testid="ops-awaitingquote">
          {applyFilter(data.awaiting_quote).slice(0, 10).map((c) => <CaseRow key={c.id} c={c} tone="teal" showWait/>)}
          <EmptyMsg show={applyFilter(data.awaiting_quote).length === 0} text="No contractors blocking us."/>
        </Queue>

        <Queue id="awaiting-approval" title="Awaiting your approval · quote on file"
               subtitle="Reviewer needed. Open and decide."
               icon={<BadgeCheck size={14}/>}
               accent="bg-[#064E2E]"
               count={h.awaiting_approval} testid="ops-awaitingapproval">
          {applyFilter(data.awaiting_approval).slice(0, 10).map((c) => <CaseRow key={c.id} c={c} tone="green" showWait/>)}
          <EmptyMsg show={applyFilter(data.awaiting_approval).length === 0} text="Approval pile is clear."/>
        </Queue>

        <Queue id="awaiting-invoice" title="Awaiting invoice from contractor"
               subtitle="Job done, no bill received."
               icon={<FilePlus2 size={14}/>}
               accent="bg-[#0F766E]"
               count={h.awaiting_invoice} testid="ops-awaitinginvoice">
          {applyFilter(data.awaiting_invoice).slice(0, 8).map((c) => <CaseRow key={c.id} c={c} tone="teal" showWait/>)}
          <EmptyMsg show={applyFilter(data.awaiting_invoice).length === 0} text="No pending invoices."/>
        </Queue>

        <Queue id="awaiting-info" title="Awaiting customer info"
               subtitle="Customer has the ball. Send a follow-up."
               icon={<MailQuestion size={14}/>}
               accent="bg-[#475569]"
               count={h.awaiting_info} testid="ops-awaitinginfo">
          {applyFilter(data.awaiting_info).slice(0, 8).map((c) => <CaseRow key={c.id} c={c} tone="slate" showWait/>)}
          <EmptyMsg show={applyFilter(data.awaiting_info).length === 0} text="No customers blocking us."/>
        </Queue>

        <Queue id="invoices" title="Blocked invoices · 7+ days unpaid"
               subtitle="Sent, not paid. Chase the customer or reissue."
               icon={<TrendingDown size={14}/>}
               accent="bg-[#B88A2A]"
               count={h.blocked_invoices} testid="ops-blocked-invoices"
               wide>
          {data.blocked_invoices.slice(0, 10).map((i) => <InvoiceRow key={i.id} i={i}/>)}
          <EmptyMsg show={data.blocked_invoices.length === 0} text="No invoices stuck. Cash flow looks clean."/>
        </Queue>

        <Queue id="contractors" title="Contractors causing issues"
               subtitle="Health score, open issues and overdue jobs combined."
               icon={<HardHat size={14}/>}
               accent="bg-[#B88A2A]"
               count={h.contractor_issues} testid="ops-contractor-issues">
          {data.contractor_issues.slice(0, 10).map((c) => <ContractorRow key={c.id} c={c}/>)}
          <EmptyMsg show={data.contractor_issues.length === 0} text="No contractors on watch."/>
        </Queue>

        <Queue id="resident" title="Resident replies in last 24h"
               subtitle="They reached out. Respond same-day."
               icon={<MessageSquare size={14}/>}
               accent="bg-[#0F766E]"
               count={h.resident_updates} testid="ops-resident-updates"
               wide>
          {data.resident_updates.slice(0, 8).map((u) => <ResidentRow key={u.note_id} u={u}/>)}
          <EmptyMsg show={data.resident_updates.length === 0} text="Inbox empty. Nothing to reply to."/>
        </Queue>

        <Queue id="new-today" title="Today's new arrivals"
               subtitle="Cases that landed since midnight."
               icon={<Activity size={14}/>}
               accent="bg-[#064E2E]"
               count={h.new_today} testid="ops-new-today">
          {applyFilter(data.new_today).slice(0, 12).map((c) => <CaseRow key={c.id} c={c} tone="green"/>)}
          <EmptyMsg show={applyFilter(data.new_today).length === 0} text="Nothing new yet today."/>
        </Queue>

        <Queue id="high-value" title="High-value live jobs"
               subtitle="Cases flagged as priority. Track personally."
               icon={<Flame size={14}/>}
               accent="bg-[#B91C1C]"
               count={h.high_value_active} testid="ops-high-value">
          {data.high_value_active.slice(0, 12).map((c) => <CaseRow key={c.id} c={c} tone="red"/>)}
          <EmptyMsg show={data.high_value_active.length === 0} text="No live priority jobs."/>
        </Queue>

      </div>
    </div>
  );
}

/* --------- Reusable row + queue building blocks ----------------------- */

function Counter({ value, label, icon, tone, anchor, testid }) {
  const palette = {
    red:    "bg-[#B91C1C]/15 border-[#B91C1C]/40 text-[#FCA5A5]",
    amber:  "bg-[#B88A2A]/15 border-[#B88A2A]/40 text-[#F6C56C]",
    primary:"bg-[#0F766E]/20 border-[#0F766E]/40 text-[#5EEAD4]",
    muted:  "bg-white/[0.04] border-white/10 text-white/55",
  }[tone];
  return (
    <a href={anchor} className={`border rounded-xl p-5 transition hover:scale-[1.02] hover:border-white/30 ${palette}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="opacity-80">{icon}</span>
        <span className="font-display text-5xl lg:text-6xl tabular-nums leading-none text-white tracking-tight">{value}</span>
      </div>
      <p className="text-[11px] tracking-[0.08em] uppercase mt-3 leading-snug font-medium">{label}</p>
    </a>
  );
}

function FilterChips({ value, onChange }) {
  const items = [
    { k: "ALL", label: "All open" },
    { k: "TODAY", label: "Today" },
    { k: "HIGH_VALUE", label: "Priority" },
  ];
  return (
    <div className="inline-flex items-center gap-1 bg-white/[0.06] border border-white/10 rounded-full p-1" data-testid="ops-filter">
      <Filter size={12} className="text-white/45 ml-2"/>
      {items.map((it) => (
        <button
          key={it.k}
          onClick={() => onChange(it.k)}
          className={`text-[11px] px-3 py-1 rounded-full transition ${value === it.k ? "bg-white text-[#071A33] font-medium" : "text-white/65 hover:text-white"}`}
          data-testid={`ops-filter-${it.k.toLowerCase()}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function Queue({ id, title, subtitle, icon, accent, count, children, testid, wide }) {
  return (
    <section id={id} className={`bg-white border border-[#E2DCD0] rounded-xl overflow-hidden flex flex-col shadow-[0_1px_0_rgba(7,26,51,0.04)] ${wide ? "lg:col-span-2" : ""}`} data-testid={testid}>
      <header className="flex items-start justify-between gap-3 p-5 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center text-white ${accent}`}>{icon}</span>
          <div>
            <h3 className="font-display text-[19px] text-[#071A33] leading-tight tracking-tight">{title}</h3>
            <p className="text-[11.5px] text-[#475569] mt-1">{subtitle}</p>
          </div>
        </div>
        <span className="font-mono text-[11px] tabular-nums bg-[#071A33] text-white px-2.5 py-1 rounded-md tracking-wider">{count}</span>
      </header>
      <div className="divide-y divide-[#E2DCD0] flex-1">{children}</div>
    </section>
  );
}

function CaseRow({ c, tone, showWait }) {
  const slaTone = SLA_TONE[c.sla_status] || SLA_TONE.GREEN;
  const sub = [c.park_name, c.contractor_name].filter(Boolean).join(" · ");
  const dotColor = {
    red: "bg-[#B91C1C]", amber: "bg-[#B88A2A]", teal: "bg-[#0F766E]", green: "bg-[#064E2E]", slate: "bg-[#475569]",
  }[tone] || "bg-[#475569]";
  return (
    <Link to={`/adminpanel/workspace/${c.id}`} className="block px-4 py-3 hover:bg-[#F7F4ED]/70 transition" data-testid={`ops-row-${c.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${dotColor}`}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-sm text-[#071A33] font-medium truncate">
              {c.full_name || "(unknown)"}{c.high_value && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] bg-[#B88A2A] text-white px-1.5 py-0.5 rounded">priority</span>}
            </p>
            <p className="text-[10px] text-[#475569] whitespace-nowrap">
              {showWait
                ? `idle ${formatHours(c.idle_hours)}`
                : c.sla_status === "RED"
                  ? `${Math.abs(c.sla_hours_remaining).toFixed(1)} h over`
                  : c.sla_status === "AMBER"
                    ? `${c.sla_hours_remaining.toFixed(1)} h left`
                    : relTime(c.created_at)}
            </p>
          </div>
          {sub && <p className="text-[11px] text-[#475569] truncate">{sub}</p>}
          <div className="flex items-center gap-2 mt-1 text-[10px] text-[#475569]">
            <span className="font-mono">{shortId(c.id)}</span>
            <span>·</span>
            <span className="uppercase tracking-[0.1em]">{STATUS_LABEL[c.status] || c.status}</span>
            {c.quote_value > 0 && <>
              <span>·</span>
              <span className="tabular-nums">{fmtMoney(c.quote_value)}</span>
            </>}
          </div>
        </div>
        <ChevronRight size={14} className="text-[#94A3B8] mt-1.5"/>
      </div>
    </Link>
  );
}

function InvoiceRow({ i }) {
  return (
    <Link to={i.case_id ? `/adminpanel/workspace/${i.case_id}` : "/adminpanel/invoices"} className="block px-4 py-3 hover:bg-[#F7F4ED]/70 transition" data-testid={`ops-inv-${i.id}`}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-2 h-2 rounded-full mt-2 bg-[#B88A2A]"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-sm text-[#071A33] font-medium truncate">{i.customer_name}<span className="text-[#475569] font-normal"> · {i.invoice_number}</span></p>
            <p className="text-[10px] text-[#B91C1C] tabular-nums whitespace-nowrap">{i.days_outstanding}d unpaid</p>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-[#475569]">
            <span className="tabular-nums">{fmtMoney(i.total)}</span>
            <span>·</span>
            <span className="truncate">{i.customer_email}</span>
          </div>
        </div>
        <ChevronRight size={14} className="text-[#94A3B8] mt-1.5"/>
      </div>
    </Link>
  );
}

function ContractorRow({ c }) {
  const score = c.health_score;
  const tone = score == null ? "bg-[#475569]"
             : score < 50 ? "bg-[#B91C1C]"
             : score < 70 ? "bg-[#B88A2A]"
             : "bg-[#064E2E]";
  return (
    <Link to={`/adminpanel/quote-reviews`} className="block px-4 py-3 hover:bg-[#F7F4ED]/70 transition" data-testid={`ops-ct-${c.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 w-7 h-7 rounded-md text-white text-[10px] font-medium flex items-center justify-center tabular-nums ${tone}`}>{score ?? "—"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-sm text-[#071A33] font-medium truncate">{c.name}<span className="text-[#475569] font-normal text-xs"> · {c.status}</span></p>
            <p className="text-[10px] text-[#475569] whitespace-nowrap">{c.active_jobs} live</p>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-[#475569]">
            {c.primary_trade && <><span className="truncate">{c.primary_trade}</span><span>·</span></>}
            {c.overdue_jobs > 0 && <span className="text-[#B91C1C] font-medium">{c.overdue_jobs} overdue</span>}
            {c.overdue_jobs > 0 && c.open_issues > 0 && <span>·</span>}
            {c.open_issues > 0 && <span className="text-[#B88A2A] font-medium">{c.open_issues} open issue{c.open_issues === 1 ? "" : "s"}</span>}
          </div>
        </div>
        <ChevronRight size={14} className="text-[#94A3B8] mt-1.5"/>
      </div>
    </Link>
  );
}

function ResidentRow({ u }) {
  return (
    <Link to={`/adminpanel/workspace/${u.case_id}`} className="block px-4 py-3 hover:bg-[#F7F4ED]/70 transition" data-testid={`ops-res-${u.note_id}`}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-2 h-2 rounded-full mt-2 bg-[#0F766E]"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <p className="text-sm text-[#071A33] font-medium truncate">{u.case_name || "(unknown)"}{u.park_name && <span className="text-[#475569] font-normal"> · {u.park_name}</span>}</p>
            <p className="text-[10px] text-[#475569] whitespace-nowrap">{relTime(u.created_at)}</p>
          </div>
          <p className="text-xs text-[#071A33] mt-1 line-clamp-2 leading-snug">{u.body}</p>
        </div>
        <ChevronRight size={14} className="text-[#94A3B8] mt-1.5"/>
      </div>
    </Link>
  );
}

function EmptyMsg({ show, text }) {
  if (!show) return null;
  return <p className="px-4 py-6 text-center text-xs text-[#475569] italic">{text}</p>;
}

function formatHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${Math.floor(h / 24)}d`;
}

function MembershipTile({ label, value, icon, tone, link, testid }) {
  const cls = tone === "green" ? "border-[#064E2E]/30 bg-[#064E2E]/5"
            : tone === "teal"  ? "border-[#0F766E]/30 bg-[#0F766E]/5"
            : tone === "amber" ? "border-[#B88A2A]/30 bg-[#B88A2A]/5"
            : tone === "red"   ? "border-[#B91C1C]/30 bg-[#B91C1C]/5"
            : "border-[#E2DCD0] bg-white";
  const inner = (
    <>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] flex items-center gap-1.5">{icon}{label}</p>
      <p className="font-display text-3xl text-[#071A33] tabular-nums mt-1 tracking-tight">{value}</p>
    </>
  );
  return link ? (
    <Link to={link} className={`block border rounded-xl p-4 transition hover:shadow-md ${cls}`} data-testid={testid}>{inner}</Link>
  ) : (
    <div className={`border rounded-xl p-4 ${cls}`} data-testid={testid}>{inner}</div>
  );
}
